import React, { useEffect, useMemo, useState } from 'react'
import { FiFilter } from 'react-icons/fi'
import { useOrder } from '../../hooks/useAuth'
import '../home/gallery.styles.scss'

const CATEGORIES = [
  'Cupcake',
  'Birthday Cake',
  'Anniversary Cake',
  'Celebration Cake',
  'Wedding Cake',
  'Pastries',
  'Custom',
]

const PAGE_SIZE = 8

const collectUrls = (item) => {
  if (!item || item.del === 'yes') return []

  if (Array.isArray(item.urls)) {
    return item.urls.filter((url) => typeof url === 'string' && url.trim())
  }

  if (typeof item.url === 'string' && item.url.trim()) {
    return [item.url.trim()]
  }

  if (typeof item.imageLink === 'string' && item.imageLink.trim()) {
    return [item.imageLink.trim()]
  }

  if (typeof item.img === 'string' && item.img.trim()) {
    return [item.img.trim()]
  }

  return []
}

const GalleryItems = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [cat, setCat] = useState('0')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const { gallery, getGallery } = useOrder()

  const openImage = (imageLink) => {
    setSelectedImage(imageLink)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setSelectedImage(null)
  }

  useEffect(() => {
    if ((!gallery || gallery.length === 0) && typeof getGallery === 'function') {
      getGallery()
    }
  }, [gallery, getGallery])

  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [cat])

  useEffect(() => {
    if (!isModalOpen) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeModal()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isModalOpen])

  const filteredGallery = useMemo(() => {
    const list = Array.isArray(gallery) ? gallery : []
    if (cat === '0') return list
    return list.filter((item) => item.cat === cat)
  }, [gallery, cat])

  const photos = useMemo(
    () =>
      filteredGallery.flatMap((item, galleryIndex) =>
        collectUrls(item).map((url, urlIndex) => ({
          url,
          key: `${item.id || galleryIndex}-${urlIndex}`,
          cat: item.cat,
        }))
      ),
    [filteredGallery]
  )

  const visiblePhotos = photos.slice(0, visibleCount)
  const remaining = Math.max(photos.length - visibleCount, 0)

  return (
    <>
      <div className="gallery-v2__toolbar">
        <label className="gallery-v2__filter">
          <span className="gallery-v2__filter-icon" aria-hidden="true">
            <FiFilter size={14} />
          </span>
          <span className="gallery-v2__filter-body">
            <span className="gallery-v2__filter-label">Browse by</span>
            <select
              value={cat}
              onChange={(event) => setCat(event.target.value)}
              name="gallery_cat"
            >
              <option value="0">All categories</option>
              {CATEGORIES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </span>
          <span className="gallery-v2__filter-chevron" aria-hidden="true" />
        </label>
        <p className="gallery-v2__count">
          <span className="gallery-v2__count-num">
            {Math.min(visibleCount, photos.length)}
          </span>
          {remaining > 0 ? `of ${photos.length} ` : null}
          {photos.length === 1 ? 'photo' : 'photos'}
        </p>
      </div>

      {photos.length > 0 ? (
        <>
          <div className="gallery-v2__grid">
            {visiblePhotos.map((photo) => (
              <figure key={photo.key} className="gallery-v2__shot">
                <button type="button" onClick={() => openImage(photo.url)}>
                  <img
                    src={photo.url}
                    alt={photo.cat ? `${photo.cat} bake` : 'Gallery bake'}
                    loading="lazy"
                  />
                </button>
              </figure>
            ))}
          </div>

          {remaining > 0 ? (
            <div className="gallery-v2__more">
              <button
                type="button"
                className="gallery-v2__more-btn"
                onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
              >
                Load more
                <span>{remaining} left</span>
              </button>
            </div>
          ) : null}
        </>
      ) : (
        <p className="gallery-v2__empty">
          {Array.isArray(gallery) && gallery.length > 0
            ? 'No photos in this category yet. Try All categories.'
            : 'No gallery photos found yet. Upload some from the admin Pages area.'}
        </p>
      )}

      {isModalOpen && selectedImage ? (
        <div
          className="gallery-v2__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="Gallery photo"
          onClick={closeModal}
        >
          <button type="button" className="gallery-v2__lightbox-close" onClick={closeModal}>
            Close
          </button>
          <figure
            className="gallery-v2__lightbox-frame"
            onClick={(event) => event.stopPropagation()}
          >
            <img src={selectedImage} alt="Selected gallery bake" />
          </figure>
        </div>
      ) : null}
    </>
  )
}

export default GalleryItems
